import { Box, OutlinedInput, Pagination, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import DateInput from '../../../components/DateInput';
import FilterSelect from '../../../components/FilterSelect';
import LoadingApp from '../../../components/LoadingApp';
import { AtestadoMedico } from '../../../interfaces/entities/AtestadoMedico';
import { FilterInterface } from '../../../interfaces/entities/FilterInterface';
import { OrderingAtestadosEnum } from '../../../interfaces/enum/OrderingAtestadosEnum';
import { getAtestado, getMeusAtestados } from '../../../services/AtestadoService';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { scrollTo } from '../../../utils/ScrollUtil';
import AtestadoMedicoCard from './AtestadoMedicoCard';
import ErrorModal from './ErrorModal';
import { orderingOptionsDefault } from './defaultStates';
import './styles.css';
import { sxInput, sxLabel, sxLabeledOutlinedInput, sxPagination } from './sxProps';
import PageTitle from '../../../components/Accessibility/PageTitle';

function AtestadoMedicoDigital() {

  const [atestados, setAtestados] = useState<AtestadoMedico[]>([]);
  const [filteredAtestados, setFilteredAtestados] = useState<AtestadoMedico[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [orderingOptions, setOrderingOptions] = useState<FilterInterface[]>(orderingOptionsDefault);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [filterText, setFilterText] = useState<string>('');
  const [dataInicial, setDataInicial] = useState<Dayjs>();
  const [dataFinal, setDataFinal] = useState<Dayjs>();

  const scrollRef = useRef<HTMLDivElement>(null);

  const accessibility = useAccessibility();

  const fetchAtestados = async () => {
    setLoading(true);
    try {
      const result = await getMeusAtestados();
      if (result?.status === 200) {
        const listaAtestados = result.data;
        setAtestados(listaAtestados);
        setFilteredAtestados(listaAtestados);
      }
    } catch (error) {
      console.error("Erro ao buscar atestados:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAtestados();
  }, []);

  const paginatedAtestados = filteredAtestados.slice(
    (page - 1) * 5,
    page * 5
  );

  const imprimirAtestado = (idAtestado: string) => {
    setLoading(true);

    getAtestado(idAtestado)
      .then(response => {
        if (response?.data) {
          const pdf = response.data;
          const blob = base64toBlob(pdf.split(',')[1]);
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl);
        }
      })
      .catch(response => {
        const message = response?.data?.mensagem;
        setErrorMessage(message ? message : '');
        setErrorModal(true);
      })
      .finally(() => {
        setLoading(false);
      })
  }

  useEffect(() => {
    const textFiltered = filterAtestados(filterText, atestados);
    handleSearchByDate(textFiltered);
    setPage(1);
  }, [filterText, dataInicial, dataFinal]);

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo('scroll-ref');
    scrollRef?.current?.focus();
  };

  const filterAtestados = (filterText: string, atestados: AtestadoMedico[]) => {
    if (filterText.length < 3) return atestados;

    const lowercasedFilter = filterText.toLowerCase();
    return atestados.filter(({ nomeMedico, espec }) => {
      return nomeMedico.toLowerCase().includes(lowercasedFilter) || espec.toLowerCase().includes(lowercasedFilter);
    });
  };

  const handleSort = (value: string) => {
    const sorted = [...filteredAtestados].sort((a, b) => {
      const dateA = dayjs(a.dtConsulta, 'DD/MM/YYYY').valueOf();
      const dateB = dayjs(b.dtConsulta, 'DD/MM/YYYY').valueOf();

      return value === OrderingAtestadosEnum.MaisAntigo ? dateA - dateB : dateB - dateA;
    });
    setFilteredAtestados(sorted);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilteredAtestados(atestados);
    setPage(1);
    setDataFinal(undefined);
    setDataInicial(undefined);
  };

  const handleDateChange = (tipo: 'inicial' | 'final', date: Dayjs) => {
    if (tipo === 'inicial') {
      setDataInicial(date);
    } else {
      setDataFinal(date);
    }
  };

  const handleSearchByDate = (textFilteredAtestados: AtestadoMedico[]) => {
    let filtered = textFilteredAtestados;
    if (dataInicial) {
      filtered = filtered.filter(atestado =>
        dayjs(atestado.dtConsulta, 'DD/MM/YYYY').isAfter(dataInicial, 'day') || dayjs(atestado.dtConsulta, 'DD/MM/YYYY').isSame(dataInicial, 'day')
      );
    }
    if (dataFinal) {
      filtered = filtered.filter(atestado =>
        dayjs(atestado.dtConsulta, 'DD/MM/YYYY').isBefore(dataFinal, 'day') || dayjs(atestado.dtConsulta, 'DD/MM/YYYY').isSame(dataFinal, 'day')
      );
    }
    setFilteredAtestados(filtered);
  };

  return (
    <div className='atestados-container'>
      <PageTitle text="Atestado médico" />

      <div className="atestados-list">
        <div className="atestados-list-header">
          <div className="filter-medical-certificate-container">
            <Box sx={sxLabeledOutlinedInput}>
              <Typography sx={sxLabel} style={accessibility.textStyleAccessibility}>
                Pesquisar profissional ou especialidade
              </Typography>
              <label htmlFor="pesquisar-input" aria-label="pesquisar profissional ou especialidade" />
              <OutlinedInput
                type='text'
                id="pesquisar-input"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value.replace(/[^a-zA-ZçÇáéíóúÁÉÍÓÚãõÃÕ\s]/g, ''))}
                name='filtrar'
                inputProps={{ maxLength: 30 }}
                sx={sxInput}
                style={accessibility.textStyleAccessibility}
                placeholder='Exemplos: José (nome) ou Cirurgião (especialidade)'
              />
            </Box>
            <Box sx={sxLabeledOutlinedInput}>
              <Typography sx={sxLabel} style={accessibility.textStyleAccessibility}>
                Data Inicial
              </Typography>
              <DateInput
                label="Data Inicial"
                value={dataInicial || null}
                onChange={(date) => handleDateChange('inicial', date)}
              />
            </Box>
            <Box sx={sxLabeledOutlinedInput}>
              <Typography sx={sxLabel} style={accessibility.textStyleAccessibility}>
                Data Final
              </Typography>
              <DateInput
                label="Data final"
                value={dataFinal || null}
                onChange={(date) => handleDateChange('final', date)}
              />
            </Box>
            <div className="atestados-list-header-grid">
              <div className="atestados-sort">
                <FilterSelect value='Mais recente' title='Ordenar por' options={orderingOptions} disabled={false} handleChange={handleSort} />
              </div>
            </div>
          </div>
          <button className="clear-filter-button" onClick={clearFilter}>
            <u style={accessibility.textStyleAccessibility}>Limpar busca</u>
          </button>
        </div>
        <ErrorModal open={errorModal} errorMessage={errorMessage} handleCloseModal={() => setErrorModal(false)} />
        <LoadingApp showLoading={loading} />
        {filteredAtestados.length === 0 ? (
          <h1 className='empty-notifications' style={accessibility.textStyleAccessibility}>
            Você ainda não possui nenhum atestado digital.
          </h1>
        ) : (
          <div className="atestados-cards-list" id='scroll-ref' ref={scrollRef} tabIndex={-1}>
            {paginatedAtestados.map(atestado => (
              <AtestadoMedicoCard
                atestado={atestado}
                key={atestado.idAtestado}
                handleClickImprimirAtestado={() => imprimirAtestado(atestado.idAtestado)}
              />
            ))}
          </div>
        )}
        {filteredAtestados.length > 5 && (
          <div className='pagination-holder'>
            <Pagination
              count={Math.ceil(filteredAtestados.length / 5)}
              sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
              color='primary'
              page={page}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default AtestadoMedicoDigital